import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { DashboardTabs } from '@/components/ui/dashboard-tabs'
import { Button } from '@/components/ui/button'
import { PageLayout } from './layout'
import SalesVolumeChart from './reports/SalesVolumeChart'
import WeeklyOutputChart from './reports/WeeklyOutputChart'
import ProductPerformanceChart from './reports/ProductPerformanceChart'
import CustomerPerformanceChart from './reports/CustomerPerformanceChart'
import MonthlySummaryTable from './reports/MonthlySummaryTable'
import CumulativeSalesChart from './reports/CumulativeSalesChart'
import YearlyRevenueChart from './reports/YearlyRevenueChart'
import RevenuePerWeekChart from './reports/RevenuePerWeekChart'
import DateRangeSelector from './reports/DateRangeSelector'
import LeaderboardCard from './reports/LeaderboardCard'
import WeeklySalesPerformanceTable from './reports/WeeklySalesPerformanceTable'
import WeeklyStrainBreakdownTable from './reports/WeeklyStrainBreakdownTable'
import SalesVolumeChartWeekly from './reports/SalesVolumeChartWeekly'
import CumulativeTotalSoldChart from './reports/CumulativeTotalSoldChart'
import SalesVelocityChart from './reports/SalesVelocityChart'
import SeasonalDemandRadarChart from './reports/SeasonalDemandRadarChart'
import DemandByDayStackedChart from './reports/DemandByDayStackedChart'
import CustomerTypeDemandChart from './reports/CustomerTypeDemandChart'
import ProductForecastChart from './reports/ProductForecastChart'
import ForecastSummaryCards from './reports/ForecastSummaryCards'
import ForecastPeriodSelector from './reports/ForecastPeriodSelector'
import CustomerDrilldownDrawer from './reports/CustomerDrilldownDrawer'
import { BarChart3, TrendingUp, Package, Users, FileText, Sparkles } from 'lucide-react'
import './ReportsDashboard.css'

export default function ReportsDashboard({ user, onLogout, sharedReportsData = [], reportsDataLoading = false, preloadReportsData }) {
  const [activeTab, setActiveTab] = useState('summary')

  // Individual date range states for each chart
  const [cumulativeSalesDateRange, setCumulativeSalesDateRange] = useState('ytd')
  const [salesVolumeAnalysisDateRange, setSalesVolumeAnalysisDateRange] = useState('ytd')
  const [weeklyOutputDateRange, setWeeklyOutputDateRange] = useState('ytd')
  const [productAnalysisDateRange, setProductAnalysisDateRange] = useState('all')
  const [productMixDateRange, setProductMixDateRange] = useState('ytd')
  const [customerMixDateRange, setCustomerMixDateRange] = useState('ytd')

  // Forecast period state
  const [forecastPeriod, setForecastPeriod] = useState('2weeks')
  const [forecastOffset, setForecastOffset] = useState(0)
  const [customerDrawerOpen, setCustomerDrawerOpen] = useState(false)

  const tabs = [
    { value: 'summary', label: 'Summary', icon: <FileText className="h-4 w-4" /> },
    { value: 'sales', label: 'Sales Performance', icon: <TrendingUp className="h-4 w-4" /> },
    { value: 'products', label: 'Historical', icon: <Package className="h-4 w-4" /> },
    { value: 'forecasting', label: 'Forecasting', icon: <Sparkles className="h-4 w-4" /> }
  ]
  
  // Trigger data load on client-side navigation (when App-level preload didn't fire)
  useEffect(() => {
    if (sharedReportsData.length === 0 && !reportsDataLoading && preloadReportsData) {
      preloadReportsData()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  
  const navigate = useNavigate()

  const handleBackToDashboard = () => {
    navigate('/')
  }

  return (
    <PageLayout
      title="Reports & Analytics"
      user={user}
      onLogout={onLogout}
    >
      <div className="h-full overflow-y-auto">
        <div className="sales-form-container">

        <div className="space-y-6">
          <DashboardTabs
            tabs={tabs}
            value={activeTab}
            onValueChange={setActiveTab}
            size="regular"
          />

          {activeTab === 'summary' && (
            <div className="space-y-6">
            <MonthlySummaryTable showLeaderboard={false} salesData={sharedReportsData} loading={reportsDataLoading} />

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-[#009689]" />
                  Weekly Strain Breakdown
                </CardTitle>
                <CardDescription>
                  Volume sold per strain family across the rolling 26-week window
                </CardDescription>
              </CardHeader>
              <CardContent>
                <WeeklyStrainBreakdownTable loading={reportsDataLoading} />
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-[#F54A00]" />
                    Sales Overview
                  </CardTitle>
                  <CardDescription>Monthly sales volume and revenue trends</CardDescription>
                </CardHeader>
                <CardContent>
                  <SalesVolumeChart compact={true} salesData={sharedReportsData} loading={reportsDataLoading} />
                </CardContent>
              </Card>

              <LeaderboardCard data={sharedReportsData} loading={reportsDataLoading} />
            </div>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-[#6366F1]" />
                      Cumulative Sales Growth
                    </CardTitle>
                    <CardDescription>Customer sales growth over time</CardDescription>
                  </div>
                  <DateRangeSelector 
                    value={cumulativeSalesDateRange} 
                    onValueChange={setCumulativeSalesDateRange}
                    className="w-[140px]"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <CumulativeSalesChart dateRange={cumulativeSalesDateRange} salesData={sharedReportsData} loading={reportsDataLoading} />
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-[#FF8C00]" />
                    Revenue per Week Analysis
                  </CardTitle>
                  <CardDescription>Weekly revenue averages by year</CardDescription>
                </CardHeader>
                <CardContent>
                  <RevenuePerWeekChart compact={true} salesData={sharedReportsData} loading={reportsDataLoading} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-[#009689]" />
                    Yearly Revenue Analysis
                  </CardTitle>
                  <CardDescription>Total annual revenue comparison</CardDescription>
                </CardHeader>
                <CardContent>
                  <YearlyRevenueChart compact={true} salesData={sharedReportsData} loading={reportsDataLoading} />
                </CardContent>
              </Card>
            </div>
            </div>
          )}

          {activeTab === 'sales' && (
            <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-[#F54A00]" />
                  Weekly Sales Performance
                </CardTitle>
                <CardDescription>
                  Rolling 26-week performance table showing volume, value, and average selling price
                </CardDescription>
              </CardHeader>
              <CardContent>
                <WeeklySalesPerformanceTable loading={reportsDataLoading} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Sales Value vs Volume Analysis</CardTitle>
                    <CardDescription>
                      Comprehensive weekly breakdown showing revenue and volume correlation
                    </CardDescription>
                  </div>
                  <DateRangeSelector 
                    value={salesVolumeAnalysisDateRange} 
                    onValueChange={setSalesVolumeAnalysisDateRange}
                    className="w-[140px]"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <SalesVolumeChartWeekly dateRange={salesVolumeAnalysisDateRange} loading={reportsDataLoading} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="h-5 w-5 text-[#009689]" />
                      Product Mix Performance
                    </CardTitle>
                    <CardDescription>
                      Revenue breakdown by mushroom variety with trends and insights
                    </CardDescription>
                  </div>
                  <DateRangeSelector 
                    value={productMixDateRange} 
                    onValueChange={setProductMixDateRange}
                    className="w-[140px]"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <ProductPerformanceChart dateRange={productMixDateRange} salesData={sharedReportsData} loading={reportsDataLoading} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-[#3b82f6]" />
                      Customer Sales Performance
                    </CardTitle>
                    <CardDescription>
                      Revenue breakdown by customer with trends and insights
                    </CardDescription>
                  </div>
                  <DateRangeSelector
                    value={customerMixDateRange}
                    onValueChange={setCustomerMixDateRange}
                    className="w-[140px]"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <CustomerPerformanceChart dateRange={customerMixDateRange} loading={reportsDataLoading} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Weekly Output Performance</CardTitle>
                    <CardDescription>
                      Weekly production tracking with mass, value, and target analysis
                    </CardDescription>
                  </div>
                  <DateRangeSelector 
                    value={weeklyOutputDateRange} 
                    onValueChange={setWeeklyOutputDateRange}
                    className="w-[140px]"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <WeeklyOutputChart dateRange={weeklyOutputDateRange} salesData={sharedReportsData} loading={reportsDataLoading} />
              </CardContent>
            </Card>
            </div>
          )}

          {activeTab === 'products' && (
            <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Cumulative Total Sold</CardTitle>
                    <CardDescription>
                      Historical cumulative volume sold over time showing growth trends
                    </CardDescription>
                  </div>
                  <DateRangeSelector 
                    value={productAnalysisDateRange} 
                    onValueChange={setProductAnalysisDateRange}
                    className="w-[140px]"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <CumulativeTotalSoldChart dateRange={productAnalysisDateRange} loading={reportsDataLoading} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-[#F54A00]" />
                      Sales Velocity
                    </CardTitle>
                    <CardDescription>
                      Rate of sales growth showing business acceleration and deceleration patterns
                    </CardDescription>
                  </div>
                  <DateRangeSelector 
                    value={productAnalysisDateRange} 
                    onValueChange={setProductAnalysisDateRange}
                    className="w-[140px]"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <SalesVelocityChart dateRange={productAnalysisDateRange} loading={reportsDataLoading} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-[#009689]" />
                  Seasonal Demand Pattern
                </CardTitle>
                <CardDescription>
                  Monthly sales volume aggregated across all years showing seasonal variation in demand
                </CardDescription>
              </CardHeader>
              <CardContent>
                <SeasonalDemandRadarChart loading={reportsDataLoading} />
              </CardContent>
            </Card>
            </div>
          )}


          {activeTab === 'forecasting' && (
            <div className="space-y-6">
              {/* Period Selector & Customer Drill-down */}
              <div className="flex items-center justify-between flex-wrap gap-4">
                <ForecastPeriodSelector
                  period={forecastPeriod}
                  offset={forecastOffset}
                  onPeriodChange={setForecastPeriod}
                  onOffsetChange={setForecastOffset}
                />
                <Button
                  variant="outline"
                  onClick={() => setCustomerDrawerOpen(true)}
                  className="flex items-center gap-2"
                >
                  <Users className="h-4 w-4" />
                  Customer Breakdown
                </Button>
              </div>

              {/* Summary Cards */}
              <ForecastSummaryCards
                period={forecastPeriod}
                offset={forecastOffset}
              />

              {/* Demand Forecast - Stacked by Product */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-[#F54A00]" />
                    Demand Forecast by Product
                  </CardTitle>
                  <CardDescription>
                    Predicted volume breakdown by mushroom variety per day
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <DemandByDayStackedChart
                    period={forecastPeriod}
                    offset={forecastOffset}
                  />
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Customer Type Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-[#009689]" />
                      Demand by Customer Type
                    </CardTitle>
                    <CardDescription>
                      Predicted vs historical volume by customer segment
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <CustomerTypeDemandChart
                      period={forecastPeriod}
                      offset={forecastOffset}
                    />
                  </CardContent>
                </Card>

                {/* Product Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="h-5 w-5 text-[#6366F1]" />
                      Demand by Product
                    </CardTitle>
                    <CardDescription>
                      Predicted vs historical volume by mushroom variety
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ProductForecastChart
                      period={forecastPeriod}
                      offset={forecastOffset}
                    />
                  </CardContent>
                </Card>
              </div>

              {/* Customer Drill-down Drawer */}
              <CustomerDrilldownDrawer
                open={customerDrawerOpen}
                onOpenChange={setCustomerDrawerOpen}
                period={forecastPeriod}
                offset={forecastOffset}
              />
            </div>
          )}
        </div>
        </div>
      </div>
    </PageLayout>
  )
}